import React from "react";
import {withTranslation} from "react-i18next";
import AuditorCard from "./auditorCard/AuditorCard";
import CamplaCoreService from "../../services/CamplaCoreService";
import StorageService from "../../services/StorageService";
import APIErrorHelper from "../../helpers/APIErrorHelper";
import {Col, Container, Row} from "react-bootstrap";

class Auditors extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      auditorExaminations: [],
      dialogApiError: "",
      apiError: "",
    };
    this.transformData = this.transformData.bind(this);
    this.eventListener = this.eventListener.bind(this);
    this.registerEventListener = this.registerEventListener.bind(this);
  }

  async componentDidMount() {
    try {
      const cachedAuditorExams = StorageService.getReferenceType(
        "cachedAuditorExams",
      );

      this.setState({ auditorExaminations: cachedAuditorExams ?? [] });

      const fetchedExaminations = await CamplaCoreService.getAuditorExams();
      const auditorExaminations = this.transformData(fetchedExaminations);
      this.setState({ auditorExaminations });
      StorageService.set("cachedAuditorExams", auditorExaminations);
      this.registerEventListener();
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t,
        ),
      });
    }
  }

  transformData(fetchedExaminations) {
    return fetchedExaminations.map((examination, examinationIdx) => {
      const instanceIndexMap = new Map();
      const instanceStorageIndexMap = new Map();

      const examinationUpdatedAt = new Date(Date.parse(examination.updated_at));

      const instances = Array.isArray(examination.instances) ? examination.instances : [];
      const updatedInstances = instances.map((instance, instanceIdx) => {
        instanceStorageIndexMap.set(instance.storageId, instanceIdx);
        instanceIndexMap.set(instance._id, instanceIdx);

        const hasLastFileChangeSet = instance.lastFileChange !== null;
        const lastFileChangeDate = hasLastFileChangeSet
            ? new Date(Date.parse(instance.lastFileChange)) : new Date();
        const isInitialized = !hasLastFileChangeSet || lastFileChangeDate.getTime() < examinationUpdatedAt.getTime();

        return {
          ...instance,
          storage: [{lastFileChange: instance.lastFileChange}],
          lastFileChange: lastFileChangeDate,
          lastSubmission: {
            date: lastFileChangeDate,
            examinationStatus: examination.status,
            isInit: isInitialized,
            firstLoad: true
          }
        };
      });
      return {
        ...examination,
        examIdx: examinationIdx,
        instances: updatedInstances,
        instanceIndexMap,
        instanceStorageIndexMap
      };
    });
  }

  eventListener(event) {
    // Handle examination status update
    if (event.model === "examinations" && event.action === "update") {
      let auditorExaminations;
      // Check if it is an update for a known
      const idx = this.state.auditorExaminations.findIndex(e => e._id === event.object._id);
      if (idx >= 0){
        // Examination is Closed / Closing --> Removing examination
        if ((event.object.status >= 20 && event.object.status <= 23)
          || event.object.status === 60 || event.object.status === 61){
          auditorExaminations = this.state.auditorExaminations.filter(e => e._id !== event.object._id);
        }else{
          // Update the status uf an existing examination
          auditorExaminations = [...this.state.auditorExaminations];
          auditorExaminations[idx] = {
            ...this.state.auditorExaminations[idx],
            status: event.object.status,
          };
        }
        StorageService.set("cachedAuditorExams", auditorExaminations);
        this.setState({ auditorExaminations });
      }else{
        // New examination detected refetch auditors
        CamplaCoreService.getAuditorExams().then((auditorExaminations) => {
          auditorExaminations = this.transformData(auditorExaminations);
          StorageService.set("cachedAuditorExams", auditorExaminations);
          this.setState({ auditorExaminations });
        }).catch(err => {
          this.setState({
            apiError: APIErrorHelper.getError(
                err,
                () => this.setState({ apiError: "" }),
                {},
                this.props.t,
            ),
          });
        });
      }
    }

    if (event.model === "instances" && event.action === "update") {
      const instanceId = event.object?._id;
      const examId = event.object?.examinationId;
      if (!instanceId || !examId) return;

      const exams = this.state.auditorExaminations ?? [];
      const examIdx = exams.findIndex((ex) => ex?._id === examId);
      if (examIdx === -1) return;

      const exam = exams[examIdx];

      if (!exam?.instanceIndexMap?.has?.(instanceId)) return;
      const idx = exam.instanceIndexMap.get(instanceId);
      if (idx === undefined || idx === null) return;

      const oldInst = exam.instances?.[idx];
      if (!oldInst) return;

      const updatedInst = {
        ...oldInst,
        hasStarted: event.object.hasStarted,
        status: event.object.status,
        memberDeviceHealth: event.object.memberDeviceHealth,
        memberDevice: event.object.memberDevice,
        attendedAt: event.object.attendedAt
      };

      const updatedInstances = exam.instances.map((inst, i) =>
          i === idx ? updatedInst : inst,
      );

      const updatedExam = {
        ...exam,
        instances: updatedInstances,
      };

      const nextExams = [...exams];
      nextExams[examIdx] = updatedExam;

      StorageService.set("cachedAuditorExams", nextExams);

      this.setState({ auditorExaminations: nextExams });
    }


    if (event.model === "instanceStorage" && event.action === "update") {
      const storageId = event.object?._id;
      const examId = event.object?.examinationId;
      const lastFileChange = event.object?.lastFileChange;
      if (!storageId || !examId || !lastFileChange) return;

      const exams = this.state.auditorExaminations ?? [];

      const examIdx = exams.findIndex((ex) => ex?._id === examId);
      if (examIdx === -1) return;

      const exam = exams[examIdx];

      if (!exam?.instanceStorageIndexMap?.has?.(storageId)) return;
      const idx = exam.instanceStorageIndexMap.get(storageId);

      const oldInst = exam.instances?.[idx];
      if (!oldInst) return;

      const date = new Date(Date.parse(lastFileChange));

      const updatedInst = {
        ...oldInst,
        storage: [{ lastFileChange }],
        lastFileChange: date,
        lastSubmission: {
          ...(oldInst.lastSubmission ?? {}),
          date,
          examinationStatus: exam.status,
          isInit: false,
          firstLoad: false,
        },
      };

      const updatedInstances = exam.instances.map((inst, i) =>
        i === idx ? updatedInst : inst,
      );
      const updatedExam = { ...exam, instances: updatedInstances };

      const nextExams = [...exams];
      nextExams[examIdx] = updatedExam;

      StorageService.set("cachedAuditorExams", nextExams);
      this.setState({ auditorExaminations: nextExams });
    }
  }

  registerEventListener() {
    CamplaCoreService.addEventListener("auditorSSE", this.eventListener);
  }

  componentWillUnmount() {
    CamplaCoreService.removeEventListener("auditorSSE");
  }

  render() {
    const { t } = this.props;
    return (
      <div>
        <Container className="pl-0 pr-0">
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
        </Container>
        <Container className="mt-3">
          <Row className="pageTitle">
            <Col>
              <h2>{t("Auditors.Title")}</h2>
            </Col>
          </Row>
          {Array.isArray(this.state.auditorExaminations) && this.state.auditorExaminations.length > 0
            ? this.state.auditorExaminations
                .sort((a, b) => new Date(a.start) - new Date(b.start)) // sort is done here to show earliest exams first
                .map((exam, idx) => (
                  <AuditorCard key={exam._id} examination={exam} count={idx} />
                ))
            : t("Auditors.NoExaminations")}
        </Container>
      </div>
    );
  }
}

export default withTranslation()(Auditors);
